/* ============================================================
 * Scandart Favorites — localStorage-driven favorites (wishlist)
 * ------------------------------------------------------------
 * No dependencies. Loaded deferred on every page via
 * snippets/scandart-favorites-assets.liquid.
 *
 * Public API: window.ScandartFavorites { list, has, add, remove, toggle, count }
 * Storage:    localStorage key "scandart:favorites:v1"
 *             [{ handle, id, addedAt }, ...] capped at config.maxItems
 * Fallback:   in-memory store when localStorage is unavailable
 *             (private browsing, quota exceeded, disabled cookies).
 * ============================================================ */
(function () {
  'use strict';

  if (window.ScandartFavorites) return; /* guard against double load */

  var STORAGE_KEY = 'scandart:favorites:v1';
  var memoryStore = null;

  /* ---------- config (rendered by scandart-favorites-assets.liquid) ---------- */
  var config = { root: '/', cardView: 'scandart-fav-card', maxItems: 100, strings: {} };
  try {
    var cfgEl = document.querySelector('[data-scandart-favorites-config]');
    if (cfgEl) {
      var parsed = JSON.parse(cfgEl.textContent);
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.root === 'string' && parsed.root) config.root = parsed.root;
        if (typeof parsed.cardView === 'string' && parsed.cardView) config.cardView = parsed.cardView;
        var max = parseInt(parsed.maxItems, 10);
        if (max > 0) config.maxItems = max;
        if (parsed.strings && typeof parsed.strings === 'object') config.strings = parsed.strings;
      }
    }
  } catch (e) { /* keep defaults */ }
  if (config.root.charAt(config.root.length - 1) !== '/') config.root += '/';

  /* ---------- storage ---------- */
  function readRaw() {
    if (memoryStore !== null) return memoryStore;
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeRaw(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
      memoryStore = null;
    } catch (e) {
      /* private mode / quota exceeded → session-only fallback */
      memoryStore = value;
    }
  }

  function sanitize(list) {
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || typeof item.handle !== 'string' || !item.handle || seen[item.handle]) continue;
      seen[item.handle] = true;
      out.push({ handle: item.handle, id: item.id || null, addedAt: item.addedAt || 0 });
    }
    /* cap length — keep the most recently added */
    if (out.length > config.maxItems) out = out.slice(out.length - config.maxItems);
    return out;
  }

  function getList() {
    var raw = readRaw();
    if (!raw) return [];
    try {
      return sanitize(JSON.parse(raw));
    } catch (e) {
      return [];
    }
  }

  function setList(list) {
    var clean = sanitize(list);
    writeRaw(JSON.stringify(clean));
    try {
      document.dispatchEvent(new CustomEvent('scandart:favorites:change', { detail: { count: clean.length } }));
    } catch (e) { /* ignore */ }
  }

  function has(handle) {
    var list = getList();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) return true;
    }
    return false;
  }

  function add(handle, id) {
    if (!handle) return false;
    var list = getList();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) return true;
    }
    list.push({ handle: handle, id: id || null, addedAt: Date.now() });
    setList(list);
    return true;
  }

  function remove(handle) {
    setList(getList().filter(function (item) { return item.handle !== handle; }));
  }

  function toggle(handle, id) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle, id);
    return true;
  }

  function count() {
    return getList().length;
  }

  /* ---------- UI sync ---------- */
  function syncToggles(root) {
    var lookup = {};
    getList().forEach(function (item) { lookup[item.handle] = true; });
    var toggles = (root || document).querySelectorAll('[data-scandart-fav-toggle]');
    Array.prototype.forEach.call(toggles, function (button) {
      var active = !!lookup[button.getAttribute('data-handle')];
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? button.getAttribute('data-label-remove') : button.getAttribute('data-label-add');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  function syncHeader() {
    var total = count();
    var counters = document.querySelectorAll('[data-scandart-fav-count]');
    Array.prototype.forEach.call(counters, function (el) {
      el.textContent = total;
      el.hidden = total === 0;
    });
  }

  function syncAll() {
    syncToggles();
    syncHeader();
  }

  /* ---------- favorites page (/pages/favorites) ---------- */
  function maybeShowEmpty(grid) {
    var emptyEl = document.querySelector('[data-scandart-favorites-empty]');
    if (emptyEl) emptyEl.hidden = grid.querySelectorAll('[data-scandart-fav-item]').length > 0;
  }

  function removeFromFavoritesGrid(handle) {
    var grid = document.querySelector('[data-scandart-favorites-grid]');
    if (!grid) return;
    var item = grid.querySelector('[data-scandart-fav-item="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]');
    if (!item) return;
    item.classList.add('scandart-fav-removing');
    setTimeout(function () {
      item.remove();
      maybeShowEmpty(grid);
    }, 260);
  }

  function renderFavoritesPage(grid) {
    var emptyEl = document.querySelector('[data-scandart-favorites-empty]');
    var errorEl = document.querySelector('[data-scandart-favorites-error]');
    var items = getList().slice().reverse(); /* newest first */

    grid.innerHTML = '';
    if (errorEl) errorEl.hidden = true;
    if (!items.length) {
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    grid.setAttribute('aria-busy', 'true');

    var failed = 0;
    var requests = items.map(function (item) {
      var url = config.root + 'products/' + encodeURIComponent(item.handle) + '?view=' + encodeURIComponent(config.cardView);
      return fetch(url)
        .then(function (res) {
          if (res.status === 404) return { prune: true };
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text().then(function (html) { return { html: html }; });
        })
        .catch(function () {
          failed++;
          return null;
        });
    });

    Promise.all(requests).then(function (results) {
      var prune = [];
      results.forEach(function (result, index) {
        if (!result) return;
        if (result.prune) {
          /* product deleted/unavailable → drop it from storage */
          prune.push(items[index].handle);
          return;
        }
        if (result.html.indexOf('<html') !== -1) {
          /* alt view template missing — full page returned; fail soft */
          failed++;
          return;
        }
        var wrapper = document.createElement('div');
        wrapper.className = 'collection__grid__item';
        wrapper.setAttribute('data-scandart-fav-item', items[index].handle);
        wrapper.innerHTML = result.html;
        grid.appendChild(wrapper);
      });

      if (prune.length) {
        setList(getList().filter(function (item) { return prune.indexOf(item.handle) === -1; }));
      }

      grid.removeAttribute('aria-busy');
      if (!grid.querySelectorAll('[data-scandart-fav-item]').length && emptyEl) emptyEl.hidden = false;
      if (failed && errorEl && config.strings.error_loading) {
        errorEl.textContent = config.strings.error_loading;
        errorEl.hidden = false;
      }
      syncAll();
    });
  }

  function initFavoritesPage() {
    var grid = document.querySelector('[data-scandart-favorites-grid]');
    if (grid) renderFavoritesPage(grid);
  }

  /* ---------- events ---------- */

  /* Click delegation (capture phase so we beat the card's <a> navigation). */
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var button = event.target.closest('[data-scandart-fav-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-handle');
    if (!handle) return;

    var nowActive = toggle(handle, button.getAttribute('data-product-id'));

    /* instant visual feedback */
    syncAll();
    if (nowActive) {
      button.classList.add('scandart-fav-toggle--pop');
      setTimeout(function () { button.classList.remove('scandart-fav-toggle--pop'); }, 320);
    } else {
      removeFromFavoritesGrid(handle);
    }
  }, true);

  /* Cross-tab sync. */
  window.addEventListener('storage', function (event) {
    if (event.key && event.key !== STORAGE_KEY) return;
    syncAll();
    var grid = document.querySelector('[data-scandart-favorites-grid]');
    if (grid) {
      var current = {};
      getList().forEach(function (item) { current[item.handle] = true; });
      Array.prototype.forEach.call(grid.querySelectorAll('[data-scandart-fav-item]'), function (el) {
        if (!current[el.getAttribute('data-scandart-fav-item')]) el.remove();
      });
      maybeShowEmpty(grid);
    }
  });

  /* Re-sync hearts on dynamically injected cards (load-more pagination,
     quick-search results, sliders). rAF-debounced. */
  var syncQueued = false;
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
        if (!syncQueued) {
          syncQueued = true;
          requestAnimationFrame(function () {
            syncQueued = false;
            syncToggles();
          });
        }
        return;
      }
    }
  });

  /* ---------- PDP: move the heart next to the product title ---------- */
  function initPdpPlacement() {
    var button = document.querySelector('.scandart-fav-toggle--pdp');
    if (!button) return;
    var title = document.querySelector('.wt-product__info .wt-product__name') ||
      document.querySelector('h1.wt-product__name');
    if (!title || title.contains(button)) return;
    title.classList.add('scandart-fav-title-host');
    title.appendChild(button);
  }

  /* ---------- init ---------- */
  function init() {
    initPdpPlacement();
    syncAll();
    initFavoritesPage();
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ScandartFavorites = {
    list: getList,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: count
  };
})();
